import { Phone, Video, Code2, MapPin, Building2 } from "lucide-react";
import { COUNTRY_BY_CODE, fmtSalaryBand } from "@/lib/countries";

export const JOB_STATUSES = [
  { key: "draft", label: "Draft", chip: "tp-chip tp-chip-violet" },
  { key: "open", label: "Open", chip: "tp-chip tp-chip-indigo" },
  { key: "on_hold", label: "On hold", chip: "tp-chip tp-chip-amber" },
  { key: "closed", label: "Closed", chip: "tp-chip" },
];
const STATUS_BY_KEY = Object.fromEntries(JOB_STATUSES.map(s => [s.key, s]));

export const jobLocation = (j) => (j.remote ? "Remote" : [j.city, COUNTRY_BY_CODE[j.country_code]?.name].filter(Boolean).join(", "));

export const fmtPosted = (iso) => {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "Posted today" : d === 1 ? "Posted yesterday" : d < 30 ? `Posted ${d}d ago` : `Posted ${new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
};

export function StatusChip({ status, testid }) {
  const s = STATUS_BY_KEY[status] || STATUS_BY_KEY.open;
  return <span className={`${s.chip} shrink-0`} data-testid={testid}>{s.label}</span>;
}

const STAGE_ICONS = [["telephonic", Phone, "Telephonic"], ["video", Video, "Video"], ["coding", Code2, "Coding"]];

export function ClientLine({ j, className = "" }) {
  if (!j.client && !j.account) return null;
  return (
    <div className={`min-w-0 ${className}`} data-testid={`job-client-${j.id}`}>
      <div className="flex items-center gap-1.5 text-xs text-slate-600 min-w-0">
        <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        {j.client && <span className="font-medium text-slate-800 truncate">{j.client}</span>}
        {j.client && j.account && <span className="text-slate-300">·</span>}
        {j.account && <span className="truncate">{j.account}</span>}
        {j.job_type && (
          <span data-testid={`job-type-${j.id}`} className={`ml-auto shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${j.job_type === "fte" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            {j.job_type === "fte" ? "Full Time" : "Contract"}
          </span>
        )}
      </div>
      {(j.service_line || j.client_job_id) && (
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-1 pl-5 min-w-0">
          {j.service_line && <span className="truncate">{j.service_line}</span>}
          {j.service_line && j.client_job_id && <span className="text-slate-300">·</span>}
          {j.client_job_id && <span className="font-mono truncate" data-testid={`job-client-job-id-${j.id}`}>#{j.client_job_id}</span>}
        </div>
      )}
    </div>
  );
}

export function JobCardBody({ j, compact = false }) {
  const p = j.pipeline || {};
  const loc = jobLocation(j);
  const st = j.stages || {};
  return (
    <>
      <div className={`flex items-center gap-2 mb-3 min-w-0 ${compact ? "" : "pr-16"}`}>
        <StatusChip status={j.status} testid={`job-status-${j.id}`} />
        {loc && (
          <span className="text-xs text-slate-500 inline-flex items-center gap-1 truncate" data-testid={`job-location-${j.id}`}>
            <MapPin className="w-3 h-3 shrink-0" />{loc}
          </span>
        )}
      </div>
      <div className="font-display font-semibold text-lg leading-snug mb-1 line-clamp-2" data-testid={`job-title-${j.id}`}>{j.title}</div>
      <ClientLine j={j} className="mb-2" />
      {!compact && <div className="text-sm text-slate-500 line-clamp-2 mb-4">{j.description}</div>}
      {!compact && (
        <div className="flex items-center gap-1.5 mb-4" data-testid={`job-stages-${j.id}`}>
          {STAGE_ICONS.filter(([k]) => st[k]).map(([k, Icon, label]) => (
            <span key={k} title={label} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-slate-100 text-slate-600">
              <Icon className="w-3 h-3" />{label}
            </span>
          ))}
        </div>
      )}
      <div className={`grid grid-cols-3 gap-2 rounded-lg bg-slate-50 border border-[color:var(--bd-sub)] p-2.5 text-center ${compact ? "my-3" : "mb-4"}`} data-testid={`job-pipeline-${j.id}`}>
        {[["Candidates", j.candidate_count || 0, "text-slate-900"], ["Completed", p.shortlisted || 0, "text-emerald-600"], ["In progress", p.in_progress || 0, "text-indigo-600"]].map(([l, n, c]) => (
          <div key={l}>
            <div className={`font-display font-semibold text-lg leading-none ${c}`}>{n}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{l}</div>
          </div>
        ))}
      </div>
      <div className="mt-auto flex items-center justify-between gap-3 text-xs text-slate-500 pt-3 border-t border-[color:var(--bd-sub)]">
        <span className="font-medium text-slate-700 truncate" data-testid={`job-salary-${j.id}`}>{fmtSalaryBand(j.salary_min, j.salary_max, j.salary_currency, j.salary_unit) || "Salary not set"}</span>
        <span className="shrink-0" data-testid={`job-posted-${j.id}`}>{fmtPosted(j.created_at)}</span>
      </div>
    </>
  );
}
